import { AttachmentUrlIcon } from "@/components";
import useChatStore from "@/store/chatStore";
import { MimeType } from "@/types";
import {
  checkImageFromMimeType,
  checkVideoFromMimeType,
  cn,
  downloadFile,
  getMimeTypeFromUrl,
} from "@/utils";
import React, { useCallback, useMemo, useState } from "react";
import { BsCloudDownloadFill } from "react-icons/bs";
import DocumentPreviewModal, {
  getFileExtension,
  type PreviewAttachment,
} from "./DocumentPreviewModal";

interface IAttachment {
  url: string;
  type?: string;
  name?: string;
}

interface Props {
  initialAttachments: IAttachment[];
  isSender?: boolean;
}

const isLegacyDocAttachment = (attachment: IAttachment) => {
  const ext = getFileExtension(attachment);
  return attachment.type === MimeType.DOC || ext === "doc";
};

const isFePreviewableAttachment = (attachment: IAttachment) => {
  const ext = getFileExtension(attachment);
  const type = attachment.type;

  return (
    type === MimeType.PDF ||
    type === MimeType.DOCX ||
    type === MimeType.XLSX ||
    type === MimeType.XLS ||
    type === MimeType.CSV ||
    ["pdf", "docx", "xls", "xlsx", "csv"].includes(ext)
  );
};

const isClickableAttachment = (attachment: IAttachment) =>
  isFePreviewableAttachment(attachment) || isLegacyDocAttachment(attachment);

const Attachment: React.FC<Props> = ({ initialAttachments, isSender }) => {
  const [previewAttachment, setPreviewAttachment] =
    useState<PreviewAttachment | null>(null);

  // classify attachments into media and document base on mimetype
  const [media, documents] = useMemo(() => {
    const media: (IAttachment & {
      documentType: "image" | "video" | "document";
    })[] = [];
    const documents: (IAttachment & {
      documentType: "image" | "video" | "document";
    })[] = [];

    initialAttachments.forEach((attachment) => {
      const mimeType = getMimeTypeFromUrl(attachment.url);
      attachment.type = mimeType;

      if (checkImageFromMimeType(mimeType)) {
        media.push({ ...attachment, documentType: "image" });
      } else if (checkVideoFromMimeType(mimeType)) {
        media.push({ ...attachment, documentType: "video" });
      } else {
        documents.push({ ...attachment, documentType: "document" });
      }
    });

    return [media, documents];
  }, [initialAttachments]);

  // Set attachment in global state to open lightbox preview
  const { setAttachment } = useChatStore();

  // open lightbox to view attachment
  const openLightbox = useCallback(
    (currentIndex: number, attachments: IAttachment[]) => {
      setAttachment({
        attachmentList: attachments,
        currentIndex: currentIndex,
      });
    },
    [setAttachment],
  );

  // handle click attachment
  const downloadAttachment = useCallback(({ url, name }: IAttachment) => {
    downloadFile(url, name);
  }, []);

  const handleOpenDocument = useCallback(
    (attachment: IAttachment) => {
      // .doc: FE không preview được → chỉ download
      if (isLegacyDocAttachment(attachment)) {
        downloadAttachment(attachment);
        return;
      }

      if (isFePreviewableAttachment(attachment)) {
        setPreviewAttachment(attachment);
      }
    },
    [downloadAttachment],
  );

  // Display multiple attachments
  return (
    <>
      <div className="space-y-4">
        {/* Document attachments */}
        {documents.length > 0 && (
          <div
            className={cn(
              "flex flex-wrap items-center gap-4",
              isSender ? "justify-end ml-auto" : "",
            )}
          >
            {documents.map((attachment, index) => {
              // render other file types
              return (
                <AttachmentFile
                  key={index}
                  attachment={attachment}
                  onDownload={() => downloadAttachment(attachment)}
                  onOpen={
                    isClickableAttachment(attachment)
                      ? () => handleOpenDocument(attachment)
                      : undefined
                  }
                />
              );
            })}
          </div>
        )}

        {/* Media attachments */}
        {media.length > 0 && (
          <div
            className={cn(
              "flex flex-wrap items-center gap-4",
              isSender ? "justify-end" : "",
            )}
          >
            {media.map((attachment, index) => {
              return (
                <div key={index} className="relative group">
                  {/* Button download file  */}
                  <button
                    type="button"
                    className="bg-cyan text-light_default absolute top-2 right-2 p-1 rounded-full invisible group-hover:visible"
                    onClick={() => downloadAttachment(attachment)}
                  >
                    <BsCloudDownloadFill />
                  </button>

                  {/* Preview media attachment */}
                  <AttachmentUrlIcon
                    url={attachment.url}
                    mimeType={attachment.type}
                    documentType={attachment.documentType}
                    name={attachment.name}
                    onClick={() => openLightbox(index, media)}
                    className={cn(
                      "h-[100px] w-auto max-w-full cursor-pointer",
                      media.length === 1 && "h-auto",
                    )} // fixed size for multiple attachments
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DocumentPreviewModal
        open={!!previewAttachment}
        onOpenChange={(open) => {
          if (!open) setPreviewAttachment(null);
        }}
        attachment={previewAttachment}
      />
    </>
  );
};

const AttachmentFile = ({
  attachment,
  onDownload,
  onOpen,
}: {
  attachment: IAttachment & {
    documentType: "image" | "video" | "document";
  };
  onDownload: () => void;
  onOpen?: () => void;
}) => {
  return (
    <div
      className={cn(
        "relative group flex items-center bg-grey-200 rounded-lg w-fit gap-2 p-2",
        onOpen && "cursor-pointer",
      )}
      onClick={onOpen}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={
        onOpen
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      <AttachmentUrlIcon
        url={attachment.url}
        mimeType={attachment.type}
        name={attachment.name}
        className="w-6"
        documentType={attachment.documentType}
      />
      <div
        className="max-w-40 line-clamp-2 leading-5.5 wrap-break-word"
        title={attachment.name}
      >
        {attachment.name}
      </div>

      {/* Button download file  */}
      <button
        type="button"
        className="bg-cyan text-light_default absolute top-1/2 translate-y-[-50%] right-2 p-1 rounded-full invisible group-hover:visible"
        onClick={(event) => {
          event.stopPropagation();
          onDownload();
        }}
      >
        <BsCloudDownloadFill />
      </button>
    </div>
  );
};

export default Attachment;
